import { useEffect, useState } from "react"
import { FaRegCircleUser } from "react-icons/fa6"
import { RiMenu3Line } from "react-icons/ri"
import { useNavigate } from "react-router-dom";
import { Box, HStack, IconButton, Menu, Portal, Separator, Spacer, Stack } from "@chakra-ui/react"
import { ALL_STEPS } from "@/constants"
import { useAuth } from "@/contexts"
import { useLogout } from "@/hooks"
import { Deal, DealStep, StepDetail } from "@/types"
import { WhatsNextModal } from "../modals"
import { Button, PulsingDot, Text, toaster } from "../ui"

interface Props {
  deals: Deal[]
  currentDeal: Deal | null
  onDealChange: (deal: Deal) => void
  onRefresh?: () => void
  onOpenDrawer?: () => void
}

export const NavBar = ({ deals, currentDeal, onDealChange, onRefresh, onOpenDrawer }: Props) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isWhatsNextOpen, setIsWhatsNextOpen] = useState(false);
  const [currentStepDetail, setCurrentStepDetail] = useState<StepDetail | null>(null);
  const logoutMutation = useLogout();

  // During limited rollout, a locked client (no full portal access) only gets the
  // Payment Center + account/2FA. Suppress the deal-flow step tracker and the
  // links that point at gated pages so we never bounce them to /payment.
  const isLocked = user?.userType === "client" && user.fullPortalAccess === false
  const hasActiveDeal = deals.some(d => d.isActive === 1)

  useEffect(() => {
    if (currentDeal && !isLocked && currentDeal.isActive === 1) {
      const index = ALL_STEPS.findIndex(step => step.step === currentDeal.currentStep);
      setCurrentStepDetail(index !== -1 ? ALL_STEPS[index] : null);
    } else {
      setCurrentStepDetail(null);
    }
  }, [currentDeal, isLocked]);

  const handleNavBarClick = () => {
    if (isLocked) return; // locked clients have no deal-flow steps to navigate to
    onRefresh?.();
    if (!deals || deals.length === 0 || !currentStepDetail) {
      setIsWhatsNextOpen(true);
      return;
    }
    const step = currentStepDetail.step;
    switch (step) {
      case DealStep.COMPLETE_NDA_FORM:
        navigate("/compliance");
        break;
      case DealStep.SIGN_NDA:
        toaster.create({ title: "Check your email for a message from docusign@esofund.com", type: "info", duration: 5000 });
        break;
      case DealStep.UPLOAD_GRANT_DOCS:
        navigate("/documents");
        break;
      case DealStep.ACCEPT_OFFER:
        navigate("/deals");
        break;
      case DealStep.COMPLETE_BC_FORM:
        navigate("/compliance");
        break;
      case DealStep.SIGN_BC:
        toaster.create({ title: "Check your email for a message from docusign@esofund.com", type: "info", duration: 5000 });
        break;
      case DealStep.UPLOAD_CLOSING_DOCS:
        navigate("/documents");
        break;
      default:
        setIsWhatsNextOpen(true);
        break;
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  return (
    <HStack pt={2} pb={2} pr={{ base: 3, md: 5 }} bg="white" w="100%" minH="56px" borderBottom={"1px solid"} borderColor="gray.200">
      {onOpenDrawer && (
        <IconButton
          aria-label="Open menu"
          variant="ghost"
          size="sm"
          ml={2}
          onClick={onOpenDrawer}
        >
          <RiMenu3Line size={22} />
        </IconButton>
      )}
      <Box display={{ base: "none", md: "flex" }} flex={1} minW={0}>
        <Stack gap={0} mx={5} cursor={hasActiveDeal ? "pointer" : "default"} onClick={hasActiveDeal ? handleNavBarClick : undefined}>
          <HStack>
            <Text fontSize="md" fontWeight="bold" lineClamp={1}>{currentStepDetail ? currentStepDetail.title : "No Active Step"}</Text>
            {hasActiveDeal && <PulsingDot />}
          </HStack>
          <Text fontSize="xs" color="gray.500" lineClamp={1}>{currentStepDetail ? currentStepDetail.description.replace(/<[^>]*>/g, "") : "No active steps at the moment. Please reach out to your Equity Partner if you have any questions."}</Text>
        </Stack>
      </Box>
      <Spacer />
      {
        deals && deals.length > 0 && !isLocked && hasActiveDeal && (
          <Button variant="solid" size={{ base: "sm", md: "md" }} mr={{ base: 2, md: 5 }} onClick={() => { onRefresh?.(); setIsWhatsNextOpen(true) }}>
            What's Next?
          </Button>
        )
      }
      <Menu.Root>
        <Menu.Trigger asChild>
          <FaRegCircleUser size={24} cursor="pointer" />
        </Menu.Trigger>
        <Portal>
          <Menu.Positioner>
            <Menu.Content bg="darkBlue.200" w="250px">
              <Text color="white" p={2} fontWeight="bold">{`${user?.firstName} ${user?.lastName}`}</Text>
              <Separator mb={2} />
              <Menu.Item
                color="white"
                fontFamily="body"
                value="account"
                cursor="pointer"
                onClick={() => navigate("/account")}
              >
                Your Account
              </Menu.Item>
              {!isLocked && (
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="taxes"
                  cursor="pointer"
                  onClick={() => navigate("/taxes")}
                >
                  Calculate Your Taxes
                </Menu.Item>
              )}
              {!isLocked && (
                <Menu.Item
                  color="white"
                  fontFamily="body"
                  value="resources"
                  cursor="pointer"
                  onClick={() => navigate("/resources")}
                >
                  Resources
                </Menu.Item>
              )}
              <Menu.Item
                color="white"
                fontFamily="body"
                value="newsletter"
                cursor="pointer"
                onClick={() => window.open("https://esofund.beehiiv.com", "_blank", "noopener,noreferrer")}
              >
                Newsletter
              </Menu.Item>
              <Menu.Item
                color="white"
                fontFamily="body"
                value="privacy-policy"
                cursor="pointer"
                onClick={() => window.open("https://www.esofund.com/privacy-policy", "_blank", "noopener,noreferrer")}
              >
                Privacy Policy
              </Menu.Item>
              <Menu.Item
                color="white"
                fontFamily="body"
                value="terms"
                mb={5}
                cursor="pointer"
                onClick={() => window.open("https://www.esofund.com/terms-of-use", "_blank", "noopener,noreferrer")}
              >
                Terms
              </Menu.Item>
              <Button variant="solid" m={2} color="white" onClick={handleLogout}>
                Sign Out
              </Button>
            </Menu.Content>
          </Menu.Positioner>
        </Portal>
      </Menu.Root>
      {hasActiveDeal && <WhatsNextModal deals={deals} currentDeal={currentDeal} onDealChange={onDealChange} isOpen={isWhatsNextOpen} onClose={() => setIsWhatsNextOpen(false)} />}
    </HStack>
  )
}
